import { useMemo } from "react";
import useSWR from "swr";
import type { Address } from "viem";

import { getServerUrl } from "config/backend";
import { selectChainId, selectMarketsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { NATIVE_TOKEN_ADDRESS, convertTokenAddress } from "sdk/configs/tokens";
import { getByKey } from "sdk/utils/objects";

type PositionVolumeInfosResponse = Record<Address, bigint>;

/**
 * MIGRATED: Replaced Subgraph query with /system_metrics REST endpoint.
 * Note: /system_metrics returns total dailyVolume, not per-market breakdown.
 * We distribute the total volume equally across all active markets as a fallback.
 */
export function use24hVolumes() {
  const chainId = useSelector(selectChainId);
  const marketsInfoData = useSelector(selectMarketsInfoData);

  const marketsReady = marketsInfoData ? "ready" : null;

  const { data } = useSWR<PositionVolumeInfosResponse | undefined>(
    marketsReady ? [chainId, "24hVolume", marketsReady] : null,
    async () => {
      try {
        const url = getServerUrl(chainId, "/system_metrics");
        const response = await fetch(url);
        const metrics = await response.json();

        if (!metrics || !marketsInfoData) {
          return undefined;
        }

        const marketAddresses = Object.keys(marketsInfoData) as Address[];

        // Prefer 24h per-market breakdown; fall back to all-time when 24h has no trades
        // (common on testnet where days pass between test trades)
        const activeMarketVolumes: Record<string, string> =
          metrics.marketVolumes && Object.keys(metrics.marketVolumes).length > 0
            ? metrics.marketVolumes
            : metrics.marketVolumesAllTime ?? {};

        // If we have per-market data (either 24h or all-time), use it directly
        if (Object.keys(activeMarketVolumes).length > 0) {
          return marketAddresses.reduce(
            (acc, marketAddress) => {
              const vol = activeMarketVolumes[marketAddress.toLowerCase()] ?? activeMarketVolumes[marketAddress];
              acc[marketAddress] = vol ? BigInt(vol) : 0n;
              return acc;
            },
            {} as Record<Address, bigint>
          );
        }

        // No per-market data available from backend for this window.
        // Return explicit zeroes instead of synthesizing equal distribution.
        return marketAddresses.reduce(
          (acc, marketAddress) => {
            acc[marketAddress] = 0n;
            return acc;
          },
          {} as Record<Address, bigint>
        );
      } catch (e) {
        console.error("Failed to fetch 24h volumes", e);
        return undefined;
      }
    },
    {
      refreshInterval: 60_000,
    }
  );

  return useMemo(() => {
    if (!data) {
      return {
        byIndexToken: {},
        byMarketToken: {},
      };
    }

    if (!marketsInfoData) {
      return {
        byIndexToken: {},
        byMarketToken: data,
      };
    }

    const byIndexToken: PositionVolumeInfosResponse = {};

    Object.entries(data).forEach(([market, volume]) => {
      const marketInfo = getByKey(marketsInfoData, market);

      if (!marketInfo) {
        return;
      }

      const indexTokenAddress = marketInfo?.indexTokenAddress;

      if (!indexTokenAddress) {
        return;
      }

      byIndexToken[indexTokenAddress] =
        (byIndexToken[indexTokenAddress] === undefined ? 0n : byIndexToken[indexTokenAddress]) + BigInt(volume);

      if (indexTokenAddress === convertTokenAddress(chainId, NATIVE_TOKEN_ADDRESS, "wrapped")) {
        byIndexToken[NATIVE_TOKEN_ADDRESS] = byIndexToken[indexTokenAddress];
      }
    });

    return {
      byIndexToken,
      byMarketToken: data,
    };
  }, [data, marketsInfoData, chainId]);
}
